import { NotificationView } from '../api';
import { functionWord, isFunction } from '../functions/functions';

/*
 * Как уведомление выглядит человеку. Одно место на два экрана: колокольчик
 * рисует строкой, чат уведомлений — карточкой (решение Ильи 10.09.2026).
 * Появился новый тип события — сюда добавляется его вид, и он сразу
 * оказывается в обоих местах.
 *
 * Заголовок отвечает на вопрос «о чём это»: у встреч — сама встреча, у
 * упоминаний — человек, который упомянул. Подпись говорит, что случилось
 * (образец Ильи 11.09.2026).
 */

export type NotifyLook = {
  /** Значок типа: имя иконки, сами рисунки лежат в components/icons. */
  icon: 'task' | 'calendar' | 'bell' | 'mention' | 'call' | 'file';
  /** Тон подложки значка из карты статусов: пусто — акцент. */
  tone: '' | 'info' | 'ok' | 'quiet';
  title: string;
  sub: string;
};

/** Столько дней живут файлы звонка; то же число на сервере (sheets.service.ts). */
export const CALL_FILES_KEEP_DAYS = 7;

const capitalize = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s);

const sheetsWord = (n: number) =>
  `${n} ${
    n % 10 === 1 && n % 100 !== 11
      ? 'лист'
      : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20)
        ? 'листа'
        : 'листов'
  }`;

export function lookOf(n: NotificationView, procName: (code: string) => string): NotifyLook {
  if (n.type === 'task') {
    return {
      icon: 'task',
      tone: 'info',
      title: n.from ?? 'Задача',
      // сервер шлёт «упоминание в задаче» — в карточке говорим о человеке,
      // как в остальных видах: «Упомянул вас в…»
      sub: `${capitalize(
        !n.text || n.text === 'упоминание в задаче' ? 'упомянул вас в задаче' : n.text,
      )}${n.task ? ` «${n.task}»` : ''}${n.projectName ? ` · ${n.projectName}` : ''}`,
    };
  }
  // Автоназначенная работа — это задание, а не «событие»: так и называем, и
  // сразу пишем суть — что сделать, по какому объекту и сколько осталось.
  if (n.type === 'assignment_assigned' || n.type === 'assignment_queued') {
    const parts = [n.typeName, n.dueIn].filter(Boolean);
    return {
      icon: 'task',
      tone: n.type === 'assignment_assigned' ? 'info' : '',
      title: n.type === 'assignment_assigned' ? 'Новое задание' : 'Задание в очереди',
      sub: `${n.what || n.title || 'Задание'}${parts.length ? ` · ${parts.join(' · ')}` : ''}`,
    };
  }
  // Попал в команду работы — руками, сам, заменой или верхом новой работы (спека
  // 2026-10-02): заголовок называет, куда, подпись — на какой должности.
  if (n.type === 'team_added') {
    const where = [n.projectName, isFunction(n.fn) ? functionWord(n.fn) : ''].filter(Boolean).join(' · ');
    return {
      icon: 'task',
      tone: 'info',
      title: where ? `Вас добавили в команду: ${where}` : 'Вас добавили в команду',
      sub: n.position ?? '',
    };
  }
  if (n.type === 'calendar_event') {
    return {
      icon: 'calendar',
      tone: '',
      title: n.title ? `Встреча «${n.title}»` : 'Встреча',
      sub: `${capitalize(n.what || 'назначена')}${n.from ? ` · ${n.from}` : ''}`,
    };
  }
  if (n.type === 'calendar_reminder') {
    return {
      icon: 'bell',
      tone: 'info',
      title: n.title ? `Встреча «${n.title}»` : 'Встреча',
      sub: 'Напоминание о встрече',
    };
  }
  if (n.type === 'mention') {
    return {
      icon: 'mention',
      tone: 'quiet',
      title: n.from ?? 'Упоминание',
      sub: n.place
        ? `Упомянул вас: ${n.place}`
        : `Упомянул вас в процессе${n.proc ? ` «${procName(n.proc)}»` : ''}`,
    };
  }
  if (n.type === 'call_summary') {
    // Итоги бывают двух родов: листы доски и переписка звонка, собранного
    // вручную (у него нет карточки в чате, читать её больше неоткуда).
    const sheets = n.count ?? 0;
    const messages = n.messages ?? 0;
    /*
     * Файлы звонка живут семь дней и потом удаляются (решение Ильи
     * 15.09.2026). Уведомление старше срока вести больше некуда, и честнее
     * сказать это прямо, чем звать за файлами, которых уже нет.
     */
    const gone = Date.now() / 1000 - n.ts > CALL_FILES_KEEP_DAYS * 24 * 3600;
    const what = sheets ? `Доска: ${sheetsWord(sheets)}` : `Переписка: ${messages} ${msgWord(messages)}`;
    return {
      icon: 'call',
      tone: gone ? 'quiet' : 'ok',
      title: 'Итоги звонка',
      sub: gone ? `${what}; файлы больше недоступны` : sheets ? `${what} с записями` : what,
    };
  }
  if (
    n.type === 'contract_returned' || n.type === 'contract_signed' ||
    n.type === 'contract_version' || n.type === 'contract_cancelled' ||
    n.type === 'contract_comment_reply' || n.type === 'contract_guest_joined' ||
    n.type === 'contract_uk_signing' || n.type === 'contract_uk_reply'
  ) {
    const sub = [n.contractorName, n.subject].filter(Boolean).join(' · ');
    // Подрядчик вошёл по ссылке: держателю важно, кто именно назвался, —
    // поэтому впереди имя гостя, а за ним его компания.
    if (n.type === 'contract_guest_joined') {
      return {
        icon: 'file',
        tone: 'info',
        title: 'Подрядчик открыл договор',
        sub: [n.from, n.contractorName].filter(Boolean).join(' · '),
      };
    }
    // УК в согласовании договора фонда (часть 4): ждём подпись УК — держателю
    // и инициатору; ответила УК — держателю, с её именем впереди.
    if (n.type === 'contract_uk_signing') return { icon: 'file', tone: 'info', title: 'Ждёт подписи УК', sub };
    if (n.type === 'contract_uk_reply') {
      return {
        icon: 'file',
        tone: 'info',
        title: 'УК ответила',
        sub: [n.from, n.contractorName].filter(Boolean).join(' · '),
      };
    }
    if (n.type === 'contract_returned') return { icon: 'file', tone: '', title: 'Договор возвращён', sub };
    if (n.type === 'contract_signed') return { icon: 'file', tone: 'ok', title: 'Договор подписан', sub };
    if (n.type === 'contract_version') return { icon: 'file', tone: '', title: 'Новая версия договора', sub };
    if (n.type === 'contract_comment_reply') return { icon: 'file', tone: 'info', title: 'Ответ в вашей ветке', sub };
    return { icon: 'file', tone: '', title: 'Договор отменён', sub };
  }
  return { icon: 'file', tone: 'quiet', title: n.from ?? 'Событие', sub: n.text ?? '' };
}

const msgWord = (n: number): string => {
  const teens = n % 100;
  const last = n % 10;
  if (teens > 10 && teens < 15) return 'сообщений';
  if (last === 1) return 'сообщение';
  if (last >= 2 && last <= 4) return 'сообщения';
  return 'сообщений';
};

/** Строка предпросмотра в списке чатов: коротко, одной строкой. */
export function notificationPreview(n: NotificationView): string {
  const look = lookOf(n, (code) => code);
  return look.title && look.title !== look.sub ? `${look.title} · ${look.sub}` : look.sub;
}
